import {
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { rawApi } from '../api/client';
import type { ApiError } from '../api/errors';
import { listKey } from '../api/queryKeys';
import { CrudSection } from '../components/CrudSection';
import {
  FILED_MONTH_REASON_FIELD,
  isFiledMonthWarning,
  WritingIntoAFiledMonth,
} from '../components/WritingIntoAFiledMonth';
import { useIslandToday, useLocationId } from '../hooks/useSeedLocation';
import { formatDate, formatRateBp } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';

const GROUPS_PATH = '/admin/v1/tax/groups';
const ITEMS_PATH = '/admin/v1/catalog/items';

interface TaxRateRow {
  id: string;
  rateBp: number;
  effectiveFrom: string;
  createdAt?: string;
}

interface TaxCharge {
  component: { id: string; code: string; name: string };
  rates: TaxRateRow[];
}

interface TaxGroupFull {
  group: { id: string; name: string; description: string | null; active: boolean };
  components: TaxCharge[];
}

/** A charge charging nothing today is not "0%", it is a charge that has been stopped. */
const isStopped = (rate: TaxRateRow | undefined) => rate !== undefined && rate.rateBp === 0;

/**
 * Newest start date first; where two rates start at the same moment, the one entered last wins.
 *
 * Somebody types 16% for the first of the month, then notices it should have been 18% and types it
 * again for the same date. Without the tie-break the screen keeps showing whichever row the
 * database happened to hand back first, which is how you talk yourself into believing the
 * correction did not save.
 */
function newestFirst(rates: TaxRateRow[]): TaxRateRow[] {
  return [...rates].sort((a, b) =>
    a.effectiveFrom === b.effectiveFrom
      ? String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''))
      : b.effectiveFrom.localeCompare(a.effectiveFrom),
  );
}

/**
 * The rate a guest is actually charged right now.
 *
 * Rates are kept as a history with a start date, so the newest row is not the current one — the
 * moment a change is entered for the first of next month, the newest row is a rate nobody is
 * paying yet. This screen used to show that row under a heading that said "today", so entering
 * next year's rate made the screen state today's tax wrongly, on the one screen you would check.
 */
function rateInForce(rates: TaxRateRow[]): TaxRateRow | undefined {
  const now = new Date().toISOString();
  return newestFirst(rates).find((r) => r.effectiveFrom <= now);
}

/**
 * Rates that have been entered but have not started yet, soonest first — one per start date.
 *
 * Only one rate can ever be in force on a given day, so listing every row entered for that day
 * puts a number on screen that will never be charged. Somebody typing 16% for the first of the
 * month and then correcting it to 18% would read "16% from Aug 1" at the top of this column and
 * conclude the correction had not saved — the exact confusion the tie-break above exists to stop.
 */
function scheduledRates(rates: TaxRateRow[]): TaxRateRow[] {
  const now = new Date().toISOString();
  const winners = new Map<string, TaxRateRow>();
  for (const r of newestFirst(rates)) {
    // newestFirst hands back the last-entered rate for a date first, so the first one seen for
    // that date is the one that will actually be charged.
    if (r.effectiveFrom > now && !winners.has(r.effectiveFrom)) winners.set(r.effectiveFrom, r);
  }
  return [...winners.values()].reverse();
}

/** A picked day becomes the instant that day starts here, so a rate never lands a day early. */
function startOfDay(day: string | null): string {
  if (!day) return new Date().toISOString();
  const parsed = new Date(`${day}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
}

/**
 * Changing what a tax charges.
 *
 * The screen has always explained that a new rate applies from the date you give it and that
 * issued bills keep the old one — while offering no way to enter one. When the government moves
 * ISV the owner had a description of the right behaviour and a database console as the only way to
 * get it, so the whole screen was reading matter.
 */
function RateChangeModal({ group, onClose }: { group: TaxGroupFull; onClose: () => void }) {
  const queryClient = useQueryClient();
  const charges = group.components;
  const [chargeId, setChargeId] = useState<string | null>(
    charges.length === 1 ? (charges[0]?.component.id ?? null) : null,
  );
  const [percent, setPercent] = useState<number | string>('');
  // The BAR's calendar, not the reader's browser: `useIslandToday` carries the reasoning and the
  // six times a UTC-sliced date has answered tomorrow from six in the evening.
  const islandToday = useIslandToday();
  const [from, setFrom] = useState<string | null>(islandToday);
  const [error, setError] = useState<string | null>(null);

  const picked = charges.find((c) => c.component.id === chargeId);
  const today = picked ? rateInForce(picked.rates) : undefined;
  const typed = Number(percent);
  // Rates are whole basis points so 8.25% survives a round trip; a percentage typed here is the
  // only place that conversion happens, and it is the number every guest bill depends on.
  const newRateBp = Number.isFinite(typed) ? Math.round(typed * 100) : null;

  /**
   * A rate effective from a date inside a filed month restates every bill in it. The server refuses
   * that once naming the month; this takes the reason and sends the same change again.
   */
  const [filedMonthWarning, setFiledMonthWarning] = useState<ApiError | null>(null);

  const save = useMutation({
    mutationFn: (input: { filedMonthReason?: string } = {}) =>
      rawApi('POST', `/admin/v1/tax/components/${chargeId}/rates`, {
        rateBp: newRateBp,
        effectiveFrom: startOfDay(from),
        // Attached only once the server has asked for it — see the dialog for why never before.
        ...(input.filedMonthReason ? { [FILED_MONTH_REASON_FIELD]: input.filedMonthReason } : {}),
      }),
    onSuccess: () => {
      setFiledMonthWarning(null);
      void queryClient.invalidateQueries({ queryKey: listKey(GROUPS_PATH) });
      notifyDone(
        'Rate saved',
        `${picked?.component.name ?? 'This charge'} is ${formatRateBp(newRateBp)} from ${formatDate(
          startOfDay(from),
        )}.`,
      );
      onClose();
    },
    onError: (err: Error) => {
      if (isFiledMonthWarning(err)) return setFiledMonthWarning(err);
      notifyError(err, 'change this rate');
    },
  });

  const submit = () => {
    if (!chargeId) return setError('Choose which charge is changing');
    if (newRateBp === null || percent === '') return setError('Enter the new rate');
    if (newRateBp < 0) return setError('A rate cannot be less than zero');
    if (!from) return setError('Say what date the new rate starts');
    setError(null);
    save.mutate({});
  };

  return (
    <Modal opened onClose={onClose} title={`Change a rate in ${group.group.name}`} size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          A rate change is a new rate starting on a date, never an edit of the old one. Bills you have already
          issued keep the rate they were charged at, and every sale is taxed at whatever rate is in force the
          moment it is rung up.
        </Text>

        {charges.length > 1 ? (
          <Select
            label="Which charge"
            required
            data={charges.map((c) => {
              const now = rateInForce(c.rates);
              return {
                value: c.component.id,
                label: now ? `${c.component.name} — now ${formatRateBp(now.rateBp)}` : c.component.name,
              };
            })}
            value={chargeId}
            onChange={setChargeId}
            comboboxProps={{ withinPortal: true }}
          />
        ) : (
          <Text size="sm">
            <b>{picked?.component.name}</b>
            {today ? ` is charged at ${formatRateBp(today.rateBp)} today.` : ' has no rate yet.'}
          </Text>
        )}

        <NumberInput
          label="New rate"
          required
          value={percent}
          onChange={setPercent}
          suffix="%"
          decimalScale={2}
          min={0}
          placeholder="15"
          /* Zero is the answer to "how do I stop charging this?", and until this line said so the
             only control the owner could find was a switch on the settings screen labelled "Charge
             the tourism levy" that no code anywhere read. It has to be a DATED zero rather than a
             switch: bills already issued keep the rate they were charged at, which is what the date
             below is for and what a global on/off could never express. */
          description="What the guest is charged, as a percentage. Zero from a date stops it being charged from then on."
        />

        <DateInput
          label="In force from"
          required
          value={from}
          onChange={setFrom}
          valueFormat="D MMM YYYY"
          popoverProps={{ withinPortal: true }}
          description="A date already past is allowed — bills you have issued are never rewritten"
        />

        {/* The one mistake worth catching before it is saved is a misplaced decimal, and the only
            way to catch it is to read the change as a sentence rather than as two boxes. */}
        {picked && newRateBp !== null && percent !== '' && (
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm">
              {picked.component.name}: {today ? formatRateBp(today.rateBp) : 'nothing'} today →{' '}
              <b>{formatRateBp(newRateBp)}</b> from {formatDate(startOfDay(from))}
            </Text>
          </Paper>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            Save the new rate
          </Button>
        </Group>
      </Stack>
      <WritingIntoAFiledMonth
        warning={filedMonthWarning}
        saving={save.isPending}
        onCancel={() => setFiledMonthWarning(null)}
        onConfirm={(filedMonthReason) => save.mutate({ filedMonthReason })}
      />
    </Modal>
  );
}

/**
 * Adding a charge to a set that already exists.
 *
 * A set could only ever be given its charges at the moment it was created, so "ZOLITUR has added a
 * levy" meant creating a second set and moving the whole menu into it, item by item — which is also
 * the manoeuvre that loses the alcohol flag and quietly changes what a driver is paid on.
 */
function AddChargeModal({ group, onClose }: { group: TaxGroupFull; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [percent, setPercent] = useState<number | string>('');
  // The bar's calendar, not the reader's browser — `useIslandToday` carries the reasoning.
  const islandToday = useIslandToday();
  const [from, setFrom] = useState<string | null>(islandToday);
  const [error, setError] = useState<string | null>(null);

  const typed = Number(percent);
  const rateBp = Number.isFinite(typed) ? Math.round(typed * 100) : null;

  const save = useMutation({
    mutationFn: () =>
      rawApi('POST', `/admin/v1/tax/groups/${group.group.id}/components`, {
        code: code.trim(),
        name: name.trim(),
        rateBp,
        effectiveFrom: startOfDay(from),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(GROUPS_PATH) });
      notifyDone(
        'Charge added',
        `${name.trim()} is charged at ${formatRateBp(rateBp)} in ${group.group.name} from ${formatDate(
          startOfDay(from),
        )}.`,
      );
      onClose();
    },
    onError: (err) => notifyError(err, 'add this charge'),
  });

  const submit = () => {
    if (!name.trim()) return setError('Say what this charge is called on the bill');
    if (!code.trim()) return setError('Give it a short code for your reports');
    if (rateBp === null || percent === '') return setError('Enter the rate');
    if (!from) return setError('Say what date it starts');
    setError(null);
    save.mutate();
  };

  return (
    <Modal opened onClose={onClose} title={`Add a charge to ${group.group.name}`} size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          The charge starts on the date you give it and appears on every bill from then on. Bills you have
          already issued are not touched.
        </Text>

        <TextInput
          label="Shown on the bill"
          required
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder="Tourism levy 4%"
        />
        <TextInput
          label="Short code"
          required
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
          placeholder="TOURISM"
          description="For your records and reports. It has to be different from the other charges in this set."
        />
        <NumberInput
          label="Rate"
          required
          value={percent}
          onChange={setPercent}
          suffix="%"
          decimalScale={2}
          min={0}
          placeholder="4"
          description="What the guest is charged, as a percentage."
        />
        <DateInput
          label="In force from"
          required
          value={from}
          onChange={setFrom}
          valueFormat="D MMM YYYY"
          popoverProps={{ withinPortal: true }}
          description="A date already past is allowed — bills you have issued are never rewritten"
        />

        {rateBp !== null && percent !== '' && name.trim() && (
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm">
              {group.group.name} will also charge <b>{name.trim()}</b> at <b>{formatRateBp(rateBp)}</b> from{' '}
              {formatDate(startOfDay(from))}
            </Text>
          </Paper>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            Add the charge
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function TaxPage() {
  const locationId = useLocationId();
  const queryClient = useQueryClient();
  const [changing, setChanging] = useState<TaxGroupFull | null>(null);
  const [addingTo, setAddingTo] = useState<TaxGroupFull | null>(null);

  /**
   * Taking a set out of use, and bringing it back.
   *
   * `active` is the column this screen has drawn a "Not in use" badge from since the badge existed,
   * and nothing anywhere could set it — a state that could not be reached, which is the same lie as
   * a rule nobody enforces. It is not a delete and must never become one: every bill ever issued
   * under a set was taxed at its rates, and those rows are what last year's return adds up from.
   */
  const setInUse = useMutation({
    mutationFn: (input: { id: string; active: boolean }) =>
      rawApi('PATCH', `/admin/v1/tax/groups/${input.id}`, { active: input.active }),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: listKey(GROUPS_PATH) });
      notifyDone(
        input.active ? 'Back in use' : 'Taken out of use',
        input.active
          ? 'Menu items can be put in this set again.'
          : 'Nothing new can be put in this set. Bills already issued keep the tax they were charged.',
      );
    },
    onError: (err) => notifyError(err, 'change whether this set is in use'),
  });

  // Keyed off the request like every other list, so adding a set below refreshes this table. Under
  // its old hand-written key the two disagreed: you added a tax set, got a green "Saved", and the
  // table above it did not change until the page was reloaded.
  const groups = useQuery({
    queryKey: listKey(GROUPS_PATH),
    queryFn: () => rawApi<TaxGroupFull[]>('GET', GROUPS_PATH),
  });

  // How many dishes a set covers is the question you ask before touching its rate — and a set
  // nothing sits in is usually a set somebody meant to put items into.
  const items = useQuery({
    queryKey: listKey(ITEMS_PATH),
    queryFn: () => rawApi<{ taxGroupId?: string }[]>('GET', ITEMS_PATH),
  });

  const itemCount = (groupId: string) =>
    items.data ? items.data.filter((i) => i.taxGroupId === groupId).length : null;

  return (
    <>
      <Title order={3} mb="md">
        Taxes
      </Title>
      <Paper withBorder p="md" radius="md" mb="md">
        <Text size="xs" c="dimmed" mb="sm">
          Every item on your menu sits in one of these sets, and the set decides the tax a guest pays on it. A
          set can carry more than one charge. When a rate changes you give it a start date, and bills you have
          already issued keep the rate they were charged at.
        </Text>
        {/*
          Where somebody goes looking for a delete button, and why there is not one. A charge is
          stopped with a nought from a date and a set is taken out of use; neither destroys the rates
          last year's bills were charged at, which is what last year's return adds up from.
        */}
        <Text size="xs" c="dimmed" mb="sm">
          Nothing here is ever deleted. To stop a charge being made, give it a rate of 0% from the day it
          stops. To stop using a whole set, take it out of use — move its items into another set first, and
          after that nothing new can be put in it.
        </Text>
        <DataTable
          idAccessor="group.id"
          records={groups.data ?? []}
          fetching={groups.isLoading}
          noRecordsText="No tax sets yet. Add one below, then put your menu items in it."
          columns={[
            {
              accessor: 'group.name',
              title: 'Set',
              render: (row: TaxGroupFull) => (
                <div>
                  <Text size="sm">{row.group.name}</Text>
                  {/* The note is asked for when a set is created and was never shown anywhere
                      afterwards, so "why does this set exist" was a question only the person who
                      typed it could answer. */}
                  {row.group.description && (
                    <Text size="xs" c="dimmed">
                      {row.group.description}
                    </Text>
                  )}
                  {/* A badge for a state nothing could set, over a rule nothing enforced. Both are
                      true now: a set with items in it cannot be taken out of use, and one that has
                      been will not accept another item — so "not in use" means nothing is sold
                      under it, rather than merely that nobody has added to it lately. */}
                  {!row.group.active && (
                    <Badge size="sm" color="gray" variant="light">
                      Not in use
                    </Badge>
                  )}
                </div>
              ),
            },
            {
              accessor: 'components',
              title: 'Charged today',
              render: (row: TaxGroupFull) => (
                <Group gap="xs">
                  {row.components.length === 0 && (
                    <Badge color="gray" variant="light">
                      Nothing charged
                    </Badge>
                  )}
                  {row.components.map((c) => {
                    const now = rateInForce(c.rates);
                    /*
                     * THREE STATES, and the third one used to read as a rate.
                     *
                     * A charge with a 0% rate in force is a charge that has been STOPPED — that is
                     * the mechanism, and the only correct one, because it is how last year's bills
                     * keep last year's tax. Printing it as "Tourism levy 0.00%" made the one action
                     * the owner had for switching a tax off look like a rate somebody had mistyped,
                     * which is how he came to be hunting for a delete button instead.
                     */
                    const stopped = isStopped(now);
                    return (
                      <Badge
                        key={c.component.id}
                        variant="light"
                        color={now ? (stopped ? 'gray' : undefined) : 'orange'}
                        // A charge whose only rate starts later is charging nothing tonight, which
                        // is worth saying out loud rather than showing as a confident 0%.
                      >
                        {!now
                          ? `${c.component.name} — not yet charged`
                          : stopped
                            ? `${c.component.name} — no longer charged since ${formatDate(now.effectiveFrom)}`
                            : `${c.component.name} ${formatRateBp(now.rateBp)}`}
                      </Badge>
                    );
                  })}
                </Group>
              ),
            },
            {
              accessor: 'scheduled',
              title: 'Changing to',
              render: (row: TaxGroupFull) => {
                const upcoming = row.components.flatMap((c) =>
                  scheduledRates(c.rates).map((r) => ({
                    key: r.id,
                    text: `${c.component.name} ${formatRateBp(r.rateBp)} from ${formatDate(r.effectiveFrom)}`,
                  })),
                );
                if (upcoming.length === 0) return null;
                return (
                  <Stack gap={2}>
                    {upcoming.map((u) => (
                      <Text key={u.key} size="sm">
                        {u.text}
                      </Text>
                    ))}
                  </Stack>
                );
              },
            },
            {
              accessor: 'usedBy',
              title: 'On the menu',
              render: (row: TaxGroupFull) => {
                const count = itemCount(row.group.id);
                if (count === null) return null;
                if (count === 0) {
                  return (
                    <Badge color="orange" variant="light">
                      No items in it
                    </Badge>
                  );
                }
                return (
                  <Text size="sm">
                    {count} {count === 1 ? 'item' : 'items'}
                  </Text>
                );
              },
            },
            {
              accessor: 'actions',
              title: '',
              textAlign: 'right',
              noWrap: true,
              render: (row: TaxGroupFull) => {
                const inUse = itemCount(row.group.id);
                return (
                  <Group gap="xs" justify="flex-end" wrap="nowrap">
                    {row.components.length > 0 && row.group.active && (
                      <Button size="xs" variant="light" onClick={() => setChanging(row)}>
                        Change a rate
                      </Button>
                    )}
                    {row.group.active && (
                      <Button size="xs" variant="subtle" onClick={() => setAddingTo(row)}>
                        Add a charge
                      </Button>
                    )}
                    {row.group.active ? (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        /* Offered and refused is how a control teaches somebody it is broken. The
                           count is already on this row, so the reason can be said before the press
                           — the server refuses it too, which is where the rule actually lives. */
                        disabled={inUse !== null && inUse > 0}
                        loading={setInUse.isPending && setInUse.variables?.id === row.group.id}
                        onClick={() => setInUse.mutate({ id: row.group.id, active: false })}
                      >
                        {inUse !== null && inUse > 0 ? 'In use on the menu' : 'Take out of use'}
                      </Button>
                    ) : (
                      <Button
                        size="xs"
                        variant="subtle"
                        loading={setInUse.isPending && setInUse.variables?.id === row.group.id}
                        onClick={() => setInUse.mutate({ id: row.group.id, active: true })}
                      >
                        Bring back into use
                      </Button>
                    )}
                  </Group>
                );
              },
            },
          ]}
        />
      </Paper>
      <CrudSection
        title="Add a tax set"
        entityNoun="tax set"
        description="Name the set, then list what it charges."
        listPath={GROUPS_PATH}
        createPath={GROUPS_PATH}
        defaults={{ locationId }}
        // The set and its charges are created together and there is no endpoint for one charge on
        // its own, so this stays one typed block rather than a row-by-row editor.
        formOnly
        columns={[]}
        fields={[
          { name: 'name', label: 'Name this set', type: 'text', required: true },
          {
            name: 'description',
            label: 'Note',
            type: 'text',
            nullable: true,
            description: 'For your own reference. Guests never see it.',
          },
          {
            /*
             * Said once here rather than remembered on every item. What a driver is paid on used to
             * hang off a box on the item form that started unticked, so a cocktail added tonight
             * went in at the higher rate and still earned a commission the plan was written not to
             * pay — and nothing anywhere compared the two. An item can still disagree with this,
             * because a pack of cigarettes is taxed as alcohol and is not a drink.
             */
            name: 'isAlcohol',
            label: 'Things in this set are alcohol',
            type: 'boolean',
            description:
              'Anything you put in this set counts as alcohol for commission, unless you say otherwise on the item itself. Leave it off for a set that is only tobacco.',
          },
          {
            name: 'components',
            label: 'What it charges',
            type: 'rows',
            required: true,
            rowNoun: 'charge',
            // Typed as a percentage, stored as basis points. It used to be a JSON box asking for
            // "15% as 1500", which is the storage format wearing a description — and the one number
            // in this system where a dropped zero changes what every guest is charged.
            description: "One line per charge that appears on a guest's bill.",
            rowFields: [
              {
                name: 'code',
                label: 'Short code',
                type: 'text',
                placeholder: 'ISV15',
                description: 'For your records and reports',
              },
              { name: 'name', label: 'Shown on the bill', type: 'text', placeholder: 'ISV 15%' },
              { name: 'rateBp', label: 'Rate', type: 'number', unit: 'percent' },
            ],
          },
        ]}
      />

      {changing && (
        <RateChangeModal key={changing.group.id} group={changing} onClose={() => setChanging(null)} />
      )}
      {addingTo && (
        <AddChargeModal key={addingTo.group.id} group={addingTo} onClose={() => setAddingTo(null)} />
      )}
    </>
  );
}
